import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
  select,
  Store
} from "@ngrx/store";
import {
  Observable,
  of,
  throwError
} from "rxjs";
import {
  catchError,
  first,
  mergeMap,
} from "rxjs/operators";
import { StorageService } from "src/app/shared/utils/storage.service";
import { ToastAlertService } from "src/app/shared/utils/toast.service";
import { ApiEndpointService } from "../service/api-endpoint.service";
import * as fromState from "../state/auth";
import { AuthService } from "../service";
import { AutoSaveHandle } from "src/app/shared/utils/auto-save.service";
import { SKIP_FORBIDDEN_REDIRECT, SKIP_UNAUTHORIZED_REDIRECT } from "./http-context-tokens";

@Injectable()
export class AddTokenHeaderHttpRequestInterceptor implements HttpInterceptor {
  /**
   * Constructor.
   */
  constructor(private store$: Store<any>, private storageService: StorageService, private router: Router, private toastService: ToastAlertService, private authService: AuthService) {
  }

  /**
   * Intercepts all HTTP requests and adds the JWT token to the request's header if the URL
   * is a REST endpoint and not login or logout.
   */
  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
      if (!request.url.includes('tenants')) {
          // SAN-384/387/392 -- every backend call goes to this tenant's one
          // shared apiUrl (e.g. api.thub.example.com) regardless of which
          // spoke subdomain the browser is actually on, so the backend's
          // own req.hostname can never see e.g. aic.thub.example.com. Tell
          // it explicitly, the same way tenant.service.ts already tells the
          // cockpit via a URL param -- PartnerSubdomainMiddleware reads this
          // header (falling back to req.hostname when it's absent).
          request = request.clone({
              withCredentials: true,
              setHeaders: { 'x-hostname': location.hostname },
          })
      }
      // SAN-511 — captured here (synchronously, at request-dispatch time),
      // not inside the async catchError below, where AutoSaveHandle's own
      // in-flight counter would already have been decremented back to 0.
      // See auto-save.service.ts for why this is safe.
      const isAutoSaveRequest = AutoSaveHandle.isAutoSaveRequestInFlight();
      return this.addToken(request).pipe(
          first(),
          mergeMap((requestWithToken: HttpRequest<any>) => next.handle(requestWithToken).pipe((
              catchError((error: HttpErrorResponse) => {
                  if (isAutoSaveRequest && (error.status === 401 || error.status === 403)) {
                      // A background auto-save tick hit a dead/forbidden
                      // session (or, per SAN-516 follow-up, a legitimate
                      // "profile locked post-submission" rejection) — don't
                      // yank the user off the page they're editing over a
                      // silent background call. Unlike the earlier revision
                      // of this fix, this does NOT permanently stop the
                      // auto-save timer — the next interval tick tries again
                      // on its own, so an edit is picked up automatically if
                      // the underlying condition (session, lock) later
                      // clears, with no page reload needed. A manual/
                      // foreground action hitting the same error still
                      // redirects below.
                      return throwError(() => error);
                  }
                  if (error.status === 401 && request.context.get(SKIP_UNAUTHORIZED_REDIRECT)) {
                      return throwError(() => error);
                  }
                  if (error.status === 401) {
                      // this.storageService.remove('user');
                      this.storageService.clear();
                      this.toastService.showToast(error.error?.message || 'Session expired', 'error')
                      this.authService.logout();
                      this.router.navigate(['/auth/login']);
                  }
                  if (error.status === 403 && request.context.get(SKIP_FORBIDDEN_REDIRECT)) {
                      return throwError(() => error);
                  }
                  if (error.status === 403) {
                      this.toastService.showToast(error.error?.message || 'Forbidden', 'error')
                      this.router.navigate(['/']);
                  }
                  return throwError(() => error);
              })
          )))
      );
  }

  /**
   * Adds the JWT token to the request's header.
   */
  private addToken(request: HttpRequest<any>): Observable<HttpRequest<any>> {
      return of(request);
  }
}
